import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Banknote } from "lucide-react";
import {
  confirmFunding,
  controlFunding,
  getAllFundingLines,
  getFunding,
  getFundingProgress,
  previewFunding,
  recentFunding,
  reviewFunding,
} from "@/api/cfoFunding";
import type {
  CfoFundingLine,
  CfoFundingProgress,
  CfoFundingReceipt,
  CfoFundingReview,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FundingImportStep } from "./funding/FundingImportStep";
import { FundingPreviewStep } from "./funding/FundingPreviewStep";
import { FundingStepper, type FundingStep } from "./funding/FundingStepper";
import { FundingUploadStep } from "./funding/FundingUploadStep";
import { CSV_ERRORS, EVALUATION, isSettled } from "./funding/fundingLabels";
import { useFundingImportStore } from "@/stores/fundingImport.store";

const POLL_MS = 2000;

const ERRORS: Record<string, string> = {
  ...CSV_ERRORS,
  CFO_IMPORT_PREVIEW_EXPIRED: "The preview has expired: upload the file again.",
  CFO_IMPORT_PREVIEW_CHANGED: "The preview changed meanwhile. It was refreshed.",
  CFO_IMPORT_CONFLICT: "The import changed meanwhile. It was refreshed.",
  CFO_IMPORT_NOT_CONFIRMED: "This import has not been started.",
  CFO_IMPORT_ROUND_VERSION_CONFLICT: "The round changed meanwhile. It was refreshed.",
  CFO_IMPORT_COMPANY_VERSION_CONFLICT: "The company changed meanwhile. It was refreshed.",
  CFO_IMPORT_REQUIRES_CORRECTED_SOURCE: "This company needs a corrected file, not a review.",
};

function message(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string") return ERRORS[detail] ?? detail.replace(/_/g, " ").toLowerCase();
    if (error.response?.status === 403) return "You are not allowed to do this.";
  }
  return "The request failed. Try again.";
}

/**
 * Crunchbase funding imports, in three steps: upload the export, review
 * its rows as they will be recorded, then import in the background with
 * live progress, step by step. `?funding_import=<id>` reopens a given import;
 * closed while it runs, the import is followed by the dock at the bottom right,
 * whose click reopens it here.
 */
export function CfoFundingSection({
  slug,
  canImport,
  canConfirm,
}: {
  slug: string;
  canImport: boolean;
  canConfirm: boolean;
}) {
  const [params, setParams] = useSearchParams();
  const selected = params.get("funding_import");
  const [open, setOpen] = useState(Boolean(selected));
  const [receipt, setReceipt] = useState<CfoFundingReceipt | null>(null);
  const [progress, setProgress] = useState<CfoFundingProgress | null>(null);
  const [lines, setLines] = useState<CfoFundingLine[] | null>(null);
  const [history, setHistory] = useState<CfoFundingReceipt[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped when a progress read fails, so the next one is still scheduled.
  const [retry, setRetry] = useState(0);
  const current = useRef(selected);
  current.current = selected;
  const tracked = useFundingImportStore((s) => s.tracked);
  const track = useFundingImportStore((s) => s.track);
  const dismiss = useFundingImportStore((s) => s.dismiss);
  const openRequest = useFundingImportStore((s) => s.openRequest);
  const clearOpenRequest = useFundingImportStore((s) => s.clearOpenRequest);
  const setViewing = useFundingImportStore((s) => s.setViewing);

  const step: FundingStep = !receipt
    ? "upload"
    : receipt.state === "PREVIEW"
      ? "preview"
      : "import";
  const settled = receipt ? isSettled(receipt) : false;
  const evaluating =
    settled && receipt ? EVALUATION[receipt.local_status]?.tone === "busy" : false;

  const select = useCallback(
    (id: string | null) => {
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        if (id) next.set("funding_import", id);
        else next.delete("funding_import");
        return next;
      });
    },
    [setParams],
  );

  // The dock asks to show its import.
  useEffect(() => {
    if (!openRequest) return;
    select(openRequest.importId);
    setOpen(true);
    clearOpenRequest();
  }, [openRequest, select, clearOpenRequest]);

  // The history, and the selected import.
  useEffect(() => {
    if (!open) return;
    let live = true;
    void recentFunding(slug)
      .then((value) => live && setHistory(value.items))
      .catch(() => undefined);
    if (!selected) {
      setReceipt(null);
      setLines(null);
      return () => {
        live = false;
      };
    }
    getFunding(slug, selected)
      .then((value) => {
        if (live && current.current === selected) setReceipt(value);
      })
      .catch((e: unknown) => live && setError(message(e)));
    return () => {
      live = false;
    };
  }, [open, slug, selected]);

  // The rows: once for the preview, then whenever a settled import changes.
  const receiptId = receipt?.id;
  const revision = receipt?.revision;
  useEffect(() => {
    if (!open || !receiptId || !(step === "preview" || settled)) return;
    let live = true;
    getAllFundingLines(slug, receiptId)
      .then((value) => live && setLines(value))
      .catch((e: unknown) => live && setError(message(e)));
    return () => {
      live = false;
    };
  }, [open, slug, receiptId, revision, step, settled]);

  // Live progress while the import runs in the background.
  useEffect(() => {
    if (!open || !receipt || step !== "import" || (settled && !evaluating) || receipt.paused) return;
    const id = receipt.id;
    const timer = window.setTimeout(() => {
      getFunding(slug, id)
        .then((value) => {
          if (current.current === id) setReceipt(value);
        })
        .catch(() => setRetry((n) => n + 1));
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [open, slug, receipt, step, settled, evaluating, retry]);

  // The import step by step, until nothing is left that runs by itself.
  const receiptState = receipt?.state;
  const complete = progress?.complete ?? false;
  useEffect(() => {
    if (!open || !receiptId || step !== "import") {
      setProgress(null);
      return;
    }
    let live = true;
    let timer: number | undefined;
    const read = () =>
      getFundingProgress(slug, receiptId)
        .then((value) => {
          if (!live) return;
          setProgress(value);
          if (!value.complete) timer = window.setTimeout(read, POLL_MS);
        })
        .catch(() => {
          if (live) timer = window.setTimeout(read, POLL_MS * 5);
        });
    void read();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
    // A new receipt state (confirmed, applied, cancelled) restarts the reading.
  }, [open, slug, receiptId, step, receiptState]);

  // The dock does not repeat what this window shows.
  useEffect(() => {
    setViewing(open && receiptId ? receiptId : null);
  }, [open, receiptId, setViewing]);
  useEffect(() => () => setViewing(null), [setViewing]);

  // The followed import, seen here once complete, leaves the dock.
  useEffect(() => {
    if (open && complete && tracked && tracked.importId === receiptId) dismiss();
  }, [open, complete, tracked, receiptId, dismiss]);

  async function run(action: () => Promise<CfoFundingReceipt>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await action();
      setReceipt(next);
      if (next.id !== current.current) select(next.id);
    } catch (e: unknown) {
      setError(message(e));
      if (receipt) {
        void getFunding(slug, receipt.id).then(setReceipt).catch(() => undefined);
      }
    } finally {
      setBusy(false);
    }
  }

  function review(request: CfoFundingReview) {
    if (receipt) void run(() => reviewFunding(slug, receipt.id, request));
  }

  function close(next: boolean) {
    setOpen(next);
    // Still running: the dock at the bottom right follows it, and reopens it here.
    if (!next && receipt && step === "import" && !(progress?.complete ?? false)) {
      track({ slug, importId: receipt.id, filename: receipt.filename });
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Funding imports
      </Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="flex max-h-[90vh] w-[min(1100px,95vw)] max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="space-y-4 border-b border-slate-100 px-6 pb-4 pt-6 text-left">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <Banknote className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle className="text-base">Import funding rounds</DialogTitle>
                <DialogDescription className="text-xs">
                  From a Crunchbase Funding Rounds export. Nothing is recorded before you import.
                </DialogDescription>
              </div>
            </div>
            <FundingStepper step={step} />
          </DialogHeader>

          {step === "upload" && (
            <FundingUploadStep
              canImport={canImport}
              busy={busy}
              error={error}
              history={history}
              onPreview={(file) => void run(() => previewFunding(slug, file))}
              onOpen={(item) => {
                setError(null);
                setLines(null);
                setReceipt(item);
                select(item.id);
              }}
            />
          )}
          {step === "preview" && receipt && (
            <FundingPreviewStep
              receipt={receipt}
              lines={lines}
              canConfirm={canConfirm}
              busy={busy}
              error={error}
              onBack={() => {
                setError(null);
                setLines(null);
                setReceipt(null);
                select(null);
              }}
              onConfirm={() => void run(() => confirmFunding(slug, receipt))}
            />
          )}
          {step === "import" && receipt && (
            <FundingImportStep
              receipt={receipt}
              progress={progress && progress.import_id === receipt.id ? progress : null}
              lines={lines}
              canConfirm={canConfirm}
              busy={busy}
              error={error}
              onControl={(action) =>
                void run(() =>
                  controlFunding(slug, receipt.id, { expected_revision: receipt.revision, action }),
                )
              }
              onReview={review}
              onNewImport={() => {
                setError(null);
                setLines(null);
                setReceipt(null);
                select(null);
              }}
              onClose={() => close(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
